import AuthoringExperiments from 'bundles/epic/clients/Authoring';
import ContentOptimization from 'bundles/epic/clients/ContentOptimization';
import { PLATFORM_ONLY_ITEM_TYPES } from 'bundles/item-authoring/shared/platformOnlyItemTypes';
import type { ItemType } from 'bundles/item/types/ItemType';

// https://tools.coursera.org/epic/experiment/HJG-kI6uEeyAw9c5depHlw
export const isItemAuthoringFrameworkEnabledForType = (itemType: ItemType): boolean => {
  if (!itemType) {
    return false;
  }

  return (
    AuthoringExperiments.get('enabledItemAuthoringFrameworkTypes').includes?.(itemType) ||
    PLATFORM_ONLY_ITEM_TYPES.includes(itemType)
  );
};

// https://tools.coursera.org/epic/experiment/CH1RJ_xEEe666w76OVbGYQ
export const isPluginUpdatedUiEnabled = () => {
  return AuthoringExperiments.get('enablePluginUpdatedUi');
};

// https://tools.coursera.org/epic/experiment/mc7TwyjNEe-BZw7QjuOi1Q
export const isV2PluginItemAuthoringEnabled = () => {
  return AuthoringExperiments.get('enableV2PluginItemAuthoring');
};

// https://tools.coursera.org/epic/experiment/iG7-ZVamEe-USQr_6ZvHVQ
export const isCourseOutlineSummaryExportsEnabled = () => {
  return AuthoringExperiments.get('enableCourseOutlineSummaryExports');
};

// https://tools.coursera.org/epic/experiment/MqmiqHE4Ee-QdhKGhQs4zw
// Repurposed as a launch epic as of Oct 7, 2025. Launch planned on Oct 30, 2025.
export const isOutlineGenerationFromScratchLaunchEnabled = () => {
  return AuthoringExperiments.get('enableOutlineGenerationFromScratch');
};

// https://tools.coursera.org/epic/experiment/awZbpiBLEfC1iRKne8QPDw
// This is a new isOutlineGenerationFromScratchEnabled epic that uses the partnerId for the pilot.
// To support GA, we use isOutlineGenerationFromScratchLaunchEnabled that can be set to 100% rollout.
export const isOutlineGenerationFromScratchEnabled = (partnerId?: number): boolean => {
  if (!partnerId && partnerId !== 0) {
    return false;
  }

  return (
    // eslint-disable-next-line camelcase
    AuthoringExperiments.get('enableOutlineGenerationFromScratchPilot', { partner_id: partnerId.toString() }) ||
    isOutlineGenerationFromScratchLaunchEnabled()
  );
};

// https://tools.coursera.org/epic/experiment/z3n_v4aWEe-yzhJWa8b7XQ
export const isCoachForAuthorsEnabled = () => {
  return AuthoringExperiments.get('enableCoachForAuthors');
};

// https://tools.coursera.org/epic/experiment/LCpPSJSaEfC-lAr_4rnFJw (for GA)
export const isCommonCartridgeSmartIngestionLaunchEnabled = () => {
  return AuthoringExperiments.get('enableCommonCartridgeSmartIngestionLaunch');
};

// https://tools.coursera.org/epic/experiment/mlXFx4v3Ee-yzhJWa8b7XQ (for pilot with partnerId)
export const isCommonCartridgeSmartIngestionEnabled = (partnerId?: number) => {
  if (
    AuthoringExperiments.get('enableCommonCartridgeSmartIngestion') &&
    isOutlineGenerationFromScratchEnabled(partnerId)
  ) {
    return true;
  }

  return isCommonCartridgeSmartIngestionLaunchEnabled();
};

export const isCbAutoSplitEnabled = (partnerId?: number) => {
  return isOutlineGenerationFromScratchEnabled(partnerId);
};

// https://tools.coursera.org/epic/experiment/H64lR1dyEfCBFBJ2BJ8jWQ
export const isDescriptEnabledByPartnerId = (partnerId?: number) => {
  if (!partnerId && partnerId !== 0) {
    return false;
  }

  return AuthoringExperiments.get('enableDescriptIntegrationByPartnerId', { partner_id: partnerId.toString() });
};

// https://tools.coursera.org/epic/experiment/l8-MNuTLEe-6kQ5XAizh6w
export const isDescriptIntegrationEnabled = (partnerId?: number) => {
  return isDescriptEnabledByPartnerId(partnerId) && isOutlineGenerationFromScratchEnabled(partnerId);
};

export const isDescriptIntegrationForRedirect = () => {
  return AuthoringExperiments.get('enableDescriptIntegration');
};

// https://tools.coursera.org/epic/experiment/Bj6atwZ9EfC1iRKne8QPDw
export const isCourseBuilderNonEnglishSupportEnabled = (partnerId?: number) => {
  return (
    AuthoringExperiments.get('enableCourseBuilderNonEnglishSupport') && isOutlineGenerationFromScratchEnabled(partnerId)
  );
};

export const isGraphqlCreateAuthoringAssetCreationEnabled = () => {
  return AuthoringExperiments.get('enableGraphQlCreateAuthoringAssetCreationAttempt');
};

// https://tools.coursera.org/epic/experiment/LLxrUGb9EfCBFBJ2BJ8jWQ
export const isContentChangeReviewEnabled = () => ContentOptimization.get('enableContentChangeReview');

// https://tools.coursera.org/epic/experiment/s9lI3W48EfC3IA6hNZeg9Q
export const isCoachForSmartIngestionEnabled = () => {
  return AuthoringExperiments.get('enableCoachForSmartIngestion');
};

export const isGradedPluginEnabled = () => {
  return AuthoringExperiments.get('enableGradedPlugin');
};

// note: this epic is intended to gate P0
// https://tools.coursera.org/epic/experiment/ez4yS6UiEfC-lAr_4rnFJw
export const isCbVerifiedSkillsP0EnabledForUser = () => {
  return AuthoringExperiments.get('enableCbVerifiedSkills');
};

// https://tools.coursera.org/epic/experiment/zTCtqLkQEfCAfhJYNo68EQ
export const isCbVerifiedSkillsP0EnabledByPartnerId = (partnerId?: number) => {
  if (!partnerId && partnerId !== 0) {
    return false;
  }
  return AuthoringExperiments.get('enableCbVerifiedSkillsP0ByPartnerId', { partner_id: partnerId.toString() });
};

// there was a bit of back and forth for this gating so i've just left to handle gating by user or partner id
// since we already had an existing epic to handle one but not the other.
export const isCbVerifiedSkillsP0Enabled = (partnerId?: number) => {
  return isCbVerifiedSkillsP0EnabledByPartnerId(partnerId) || isCbVerifiedSkillsP0EnabledForUser();
};

// https://tools.coursera.org/epic/experiment/zTCtqLkQEfCAfhJYNo68EQ
export const isCbVerifiedSkillsP1EnabledByPartnerId = (partnerId?: number) => {
  if (!partnerId && partnerId !== 0) {
    return false;
  }
  return AuthoringExperiments.get('enableCbVerifiedSkillsP1ByPartnerId', { partner_id: partnerId.toString() });
};
